/**
 * 气象功能混入（来自 weatherData.vue）
 * 包含：气压等值线、温度瓦片、海浪瓦片、台风轨迹、潮汐站点、风力粒子、天气预报
 */
import axios from "axios";
// import * as Cesium from "cesium";

const PRESSURE_API_BASE = 'http://112.126.96.159:9091/';
const PRESSURE_APPKEY = 'f0c487dd89e69d4c82510f1f78d392ea';

export default {
  data() {
    return {
      weatherLayers: [
        { key: 'pressure', name: '气压', active: false },
        { key: 'temperature', name: '温度', active: false },
        { key: 'wave', name: '海浪', active: false },
        { key: 'typhoon', name: '台风', active: false },
        { key: 'tide', name: '潮汐', active: false },
        { key: 'wind', name: '风力', active: false },
        { key: 'forecast', name: '天气预报', active: false },
      ],
      loading: false,
      isolineData: null,
      isolinePrimitives: [],
      clickHandler: null,
      tooltip: { show: false, x: 0, y: 0, label: '' },
      tooltipTimer: null,
      pressureLegendEl: null,
      temperatureLegendEl: null,
      waveLegendEl: null,
      windLegendEl: null,
      timelineEl: null,
      currentDateIndex: 0,
      baseDate: null,
      playTimer: null,
      isPlaying: false,
      totalDays: 10,
      temperatureLayer: null,
      waveLayer: null,
      typhoonPanelEl: null,
      typhoonList: [],
      typhoonLoading: false,
      selectedTyphoon: null,
      typhoonTrackData: [],
      typhoonTrackEntities: [],
      typhoonGifOverlay: null,
      trackSimulation: null,
      typhoonNodePopup: { show: false, x: 0, y: 0, html: '' },
      typhoonClickHandler: null,
      tidePoints: [],
      tideEntities: [],
      tideLabelGroup: null,
      tideDetailPopup: { show: false, loading: false, data: null, portid: '' },
      tideChartInstance: null,
      tideClickHandler: null,
      windLayer: null,
      windLoading: false,
      forecastPanel: { show: false, loading: false, data: [], position: { lng: 116.4, lat: 39.9 } },
      forecastEntity: null,
      forecastClickHandler: null,
      forecastClickMode: false,
      selectedForecastCity: null,
      forecastCityOptions: [],
    };
  },

  computed: {
    forecastTableColumns() {
      const cols = [];
      if (!this.forecastPanel.data || !this.forecastPanel.data.length) return cols;
      this.forecastPanel.data.forEach(day => {
        if (day.hours && day.hours.length) {
          day.hours.forEach(h => {
            const hourStr = String(h.hour).padStart(2, '0') + ':00';
            cols.push({
              weekDay: day.weekDay,
              dateShort: day.dateShort + ' ' + hourStr,
              hour: h.hour,
              temperature: h.temperature,
              windSpeed: h.windSpeed,
              gustSpeed: h.gustSpeed,
              windDirection: h.windDirection,
              waveHeight: h.waveHeight,
            });
          });
        }
      });
      return cols;
    },
    forecastDays() {
      const data = this.forecastPanel.data;
      if (!data || !data.length) return [];
      return data.map(day => ({
        label: day.weekDay + '（' + day.dateShort + '）',
        colspan: day.hours ? day.hours.length : 0,
      }));
    },
    isPressureActive() {
      const pressure = this.weatherLayers.find(item => item.key === 'pressure');
      return pressure && pressure.active;
    },
  },

  methods: {
    /** 切换气象图层开关 */
    toggleLayer(item) {
      item.active = !item.active;

      if (item.key === 'pressure') {
        if (item.active) {
          this.currentDateIndex = 0;
          this.baseDate = new Date();
          this.fetchAndRenderIsoline();
          this.ensureTimeline();
        } else {
          this.clearIsolinePrimitives();
          this.unbindClick();
          this.removeTimelineIfBothOff();
        }
      }
      if (item.key === 'temperature') {
        if (item.active) {
          this.currentDateIndex = 0;
          this.baseDate = new Date();
          this.updateTemperatureLayer();
          this.ensureTimeline();
        } else {
          this.removeTemperatureLayer();
          this.removeTemperatureLegend();
          this.removeTimelineIfBothOff();
        }
      }
      if (item.key === 'wave') {
        if (item.active) {
          this.currentDateIndex = 0;
          this.baseDate = new Date();
          this.updateWaveLayer();
          this.ensureTimeline();
        } else {
          this.removeWaveLayer();
          this.removeWaveLegend();
          this.removeTimelineIfBothOff();
        }
      }
      if (item.key === 'typhoon') {
        if (item.active) { this.createTyphoonPanel(); this.fetchTyphoonList(); }
        else { this.removeTyphoonPanel(); this.typhoonList = []; this.selectedTyphoon = null; }
      }
      if (item.key === 'tide') {
        if (item.active) this.loadTideData();
        else this.removeTidePoints();
      }
      if (item.key === 'wind') {
        if (item.active) {
          this.currentDateIndex = 0;
          this.baseDate = new Date();
          this.updateWindLayer();
          this.ensureTimeline();
        } else {
          this.removeWindLayer();
          this.removeWindLegend();
          this.removeTimelineIfBothOff();
        }
      }
      if (item.key === 'forecast') {
        if (item.active) this.openForecastPanel();
        else this.closeForecastPanel();
      }
    },

    ensureTimeline() {
      if (!this.timelineEl) this.createTimeline();
    },

    removeTimelineIfBothOff() {
      const pressureActive = this.weatherLayers.find(l => l.key === 'pressure')?.active;
      const windActive = this.weatherLayers.find(l => l.key === 'wind')?.active;
      const tempActive = this.weatherLayers.find(l => l.key === 'temperature')?.active;
      const waveActive = this.weatherLayers.find(l => l.key === 'wave')?.active;
      if (!pressureActive && !tempActive && !waveActive && !windActive) this.removeTimeline();
    },

    getTimestampForIndex(index) {
      if (!this.baseDate) {
        const d = new Date();
        const utc = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 18, 0, 0));
        return Math.floor(utc.getTime() / 1000) + index * 86400;
      }
      const target = new Date(this.baseDate);
      target.setDate(target.getDate() + index);
      const utc = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), target.getUTCDate(), 18, 0, 0));
      return Math.floor(utc.getTime() / 1000);
    },

    getTodayTimestamp() {
      return this.getTimestampForIndex(this.currentDateIndex);
    },

    // ========== 气压（等值线） ==========

    async fetchAndRenderIsoline() {
      this.loading = true;
      const timestamp = this.getTodayTimestamp();
      try {
        const response = await axios.get(PRESSURE_API_BASE, {
          params: { appkey: PRESSURE_APPKEY, cmd: 'isoline', timestamp },
        });
        if (response.data && response.data.success && response.data.data) {
          this.isolineData = response.data.data;
          this.renderIsolines(response.data.data);
        }
      } catch (error) {
        console.error('等值线请求失败:', error);
      } finally {
        this.loading = false;
      }
    },

    getPressureColorKey(pressureHPa) {
      const minP = 960, maxP = 1036, buckets = 12;
      const clamped = Math.max(minP, Math.min(maxP, pressureHPa));
      return Math.min(Math.floor(((clamped - minP) / (maxP - minP)) * buckets), buckets - 1);
    },

    getColorByKey(key, totalKeys) {
      const ratio = totalKeys > 1 ? key / (totalKeys - 1) : 0;
      let r, g, b;
      if (ratio < 0.33) { const t = ratio / 0.33; r = 30; g = Math.round(144 + t * 111); b = 255; }
      else if (ratio < 0.66) { const t = (ratio - 0.33) / 0.33; r = Math.round(30 + t * 225); g = 255; b = Math.round(255 - t * 255); }
      else { const t = (ratio - 0.66) / 0.34; r = 255; g = Math.round(255 - t * 200); b = 0; }
      return new Cesium.Color(r / 255, g / 255, b / 255, 0.8);
    },

    simplifyPolyline(segments, tolerance) {
      const result = [];
      segments.forEach(segment => {
        if (!segment || segment.length <= 2) {
          if (segment && segment.length >= 2) result.push(segment);
          return;
        }
        result.push(this.douglasPeucker(segment, tolerance));
      });
      return result;
    },

    douglasPeucker(points, tolerance) {
      if (points.length <= 2) return [...points];
      let maxDist = 0, maxIdx = 0;
      const first = points[0], last = points[points.length - 1];
      for (let i = 1; i < points.length - 1; i++) {
        const dist = this.pointToLineDist(points[i], first, last);
        if (dist > maxDist) { maxDist = dist; maxIdx = i; }
      }
      if (maxDist > tolerance) {
        const left = this.douglasPeucker(points.slice(0, maxIdx + 1), tolerance);
        const right = this.douglasPeucker(points.slice(maxIdx), tolerance);
        return left.slice(0, -1).concat(right);
      }
      return [first, last];
    },

    pointToLineDist(point, lineStart, lineEnd) {
      const dx = lineEnd[0] - lineStart[0], dy = lineEnd[1] - lineStart[1];
      if (dx === 0 && dy === 0) return Math.sqrt((point[0] - lineStart[0]) ** 2 + (point[1] - lineStart[1]) ** 2);
      const t = ((point[0] - lineStart[0]) * dx + (point[1] - lineStart[1]) * dy) / (dx * dx + dy * dy);
      return Math.sqrt((point[0] - lineStart[0] - t * dx) ** 2 + (point[1] - lineStart[1] - t * dy) ** 2);
    },

    renderIsolines(data) {
      if (!window.viewer || !data || !data.isolines) {
        console.warn('viewer 或 等值线数据不可用');
        return;
      }
      this.clearIsolinePrimitives();
      const viewer = window.viewer, tolerance = 0.15;
      const usedKeys = new Set();
      data.isolines.forEach(iso => usedKeys.add(this.getPressureColorKey(iso.level / 100)));
      const sortedKeys = [...usedKeys].sort((a, b) => a - b);
      const colorGroups = {};
      sortedKeys.forEach(key => { colorGroups[key] = []; });
      data.isolines.forEach(isoline => {
        const colorKey = this.getPressureColorKey(isoline.level / 100);
        const segs = this.simplifyPolyline(isoline.segments, tolerance);
        segs.forEach(segment => {
          if (!segment || segment.length < 2) return;
          const positions = segment.map(coord => Cesium.Cartesian3.fromDegrees(coord[0], coord[1], 0));
          colorGroups[colorKey].push(new Cesium.GeometryInstance({
            id: { pressure: isoline.level / 100, level: isoline.level },
            geometry: new Cesium.GroundPolylineGeometry({ positions, width: 1.5 }),
          }));
        });
      });
      sortedKeys.forEach(key => {
        const instances = colorGroups[key];
        if (!instances || instances.length === 0) return;
        const color = this.getColorByKey(key, sortedKeys.length);
        const primitive = new Cesium.GroundPolylinePrimitive({
          geometryInstances: instances,
          appearance: new Cesium.PolylineMaterialAppearance({ material: Cesium.Material.fromType('Color', { color }) }),
          allowPicking: true, asynchronous: false,
        });
        viewer.scene.groundPrimitives.add(primitive);
        this.isolinePrimitives.push(primitive);
      });
      this.bindClick();
      this.createPressureLegend(data);
    },

    bindClick() {
      this.unbindClick();
      if (!window.viewer) return;
      const viewer = window.viewer;
      const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
      handler.setInputAction((click) => {
        const picked = viewer.scene.pick(click.position);
        if (picked && picked.id && picked.id.pressure !== undefined) {
          this.showTooltip(click.position.x, click.position.y - 40, `${picked.id.pressure.toFixed(0)} hPa`);
        } else { this.hideTooltip(); }
      }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
      handler.setInputAction((movement) => {
        const picked = viewer.scene.pick(movement.endPosition);
        viewer.scene.canvas.style.cursor = (picked && picked.id && picked.id.pressure !== undefined) ? 'pointer' : 'default';
      }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
      this.clickHandler = handler;
    },

    unbindClick() {
      if (this.clickHandler) { this.clickHandler.destroy(); this.clickHandler = null; }
      this.hideTooltip();
    },

    showTooltip(x, y, label) {
      if (this.tooltipTimer) clearTimeout(this.tooltipTimer);
      this.tooltip = { show: true, x, y, label };
    },

    hideTooltip() {
      if (this.tooltipTimer) clearTimeout(this.tooltipTimer);
      this.tooltipTimer = setTimeout(() => { this.tooltip.show = false; }, 200);
    },

    clearIsolinePrimitives() {
      this.removePressureLegend();
      if (window.viewer && this.isolinePrimitives.length) {
        this.isolinePrimitives.forEach(p => {
          try { window.viewer.scene.groundPrimitives.remove(p); } catch (e) { }
        });
      }
      this.isolinePrimitives = [];
    },

    // ========== 时间轴 ==========

    createTimeline() {
      this.removeTimeline();
      if (!window.viewer) return;

      const total = this.totalDays;
      const baseDate = this.baseDate ? new Date(this.baseDate) : new Date();
      baseDate.setHours(0, 0, 0, 0);

      let markersHtml = '';
      for (let i = 0; i <= total; i++) {
        const d = new Date(baseDate);
        d.setDate(d.getDate() + i);
        const label = `${d.getMonth() + 1}/${d.getDate()}`;
        markersHtml += `<span class="wtl-marker" data-idx="${i}">${label}</span>`;
      }

      const el = document.createElement('div');
      el.className = 'weather-timeline';
      el.innerHTML = `
        <div class="wtl-header">
          <button class="wtl-play-btn" id="wtl-play-btn">▶</button>
          <div class="wtl-current-label" id="wtl-current-label"></div>
        </div>
        <div class="wtl-slider-container">
          <div class="wtl-slider-track"></div>
          <div class="wtl-slider-fill" id="wtl-slider-fill"></div>
          <div class="wtl-slider-thumb" id="wtl-slider-thumb"></div>
        </div>
        <div class="wtl-markers">
          ${markersHtml}
        </div>
      `;

      // 注入样式
      if (!document.getElementById('weather-timeline-style')) {
        const style = document.createElement('style');
        style.id = 'weather-timeline-style';
        style.textContent = `
          .weather-timeline {
            position: absolute;
            left: 50%;
            bottom: 84px;
            transform: translateX(-50%);
            width: 480px;
            background: rgba(0, 0, 0, 0.7);
            border-radius: 8px;
            padding: 8px 14px 6px;
            z-index: 100;
          }
          .wtl-header {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 6px;
          }
          .wtl-play-btn {
            background: rgba(255,255,255,0.15);
            color: #fff;
            border: none;
            width: 28px;
            height: 28px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .wtl-play-btn:hover { background: rgba(255,255,255,0.3); }
          .wtl-play-btn.playing { color: #1890ff; }
          .wtl-current-label {
            color: #fff;
            font-size: 13px;
            font-weight: bold;
          }
          .wtl-slider-container {
            position: relative;
            height: 6px;
            margin: 4px 0;
          }
          .wtl-slider-track {
            position: absolute;
            top: 0; left: 0; right: 0;
            height: 100%;
            background: rgba(255,255,255,0.2);
            border-radius: 3px;
          }
          .wtl-slider-fill {
            position: absolute;
            top: 0; left: 0;
            height: 100%;
            background: #1890ff;
            border-radius: 3px;
            width: 0%;
            transition: width 0.2s;
          }
          .wtl-slider-thumb {
            position: absolute;
            top: 50%;
            width: 14px;
            height: 14px;
            background: #fff;
            border: 2px solid #1890ff;
            border-radius: 50%;
            transform: translate(-50%, -50%);
            left: 0%;
            cursor: pointer;
            transition: left 0.2s;
            z-index: 1;
          }
          .wtl-markers {
            display: flex;
            justify-content: space-between;
            margin-top: 2px;
          }
          .wtl-marker {
            color: rgba(255,255,255,0.5);
            font-size: 10px;
            cursor: pointer;
            user-select: none;
            transition: color 0.2s;
          }
          .wtl-marker:hover { color: #fff; }
          .wtl-marker.active { color: #1890ff; font-weight: bold; }
        `;
        document.head.appendChild(style);
      }

      window.viewer.container.appendChild(el);

      // 绑定事件
      const sliderContainer = el.querySelector('.wtl-slider-container');
      const thumb = el.querySelector('#wtl-slider-thumb');
      const fill = el.querySelector('#wtl-slider-fill');
      const label = el.querySelector('#wtl-current-label');
      const markers = el.querySelectorAll('.wtl-marker');
      const playBtn = el.querySelector('#wtl-play-btn');

      const updateUI = () => {
        const pct = (this.currentDateIndex / total) * 100;
        fill.style.width = `${pct}%`;
        thumb.style.left = `${pct}%`;
        const d = new Date(baseDate);
        d.setDate(d.getDate() + this.currentDateIndex);
        label.textContent = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        markers.forEach((m, i) => {
          m.classList.toggle('active', i === this.currentDateIndex);
        });
      };

      updateUI();

      // 滑块拖拽
      let dragging = false;
      const onDrag = (e) => {
        if (!dragging) return;
        const rect = sliderContainer.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        const idx = Math.round(ratio * total);
        if (idx !== this.currentDateIndex) {
          this.currentDateIndex = idx;
          this.weatherOnDateChange(updateUI);
        }
      };
      const startDrag = () => { dragging = true; };
      const endDrag = () => { dragging = false; };

      thumb.addEventListener('mousedown', startDrag);
      sliderContainer.addEventListener('mousedown', (e) => {
        if (e.target === thumb) return;
        const rect = sliderContainer.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const idx = Math.round(ratio * total);
        this.currentDateIndex = idx;
        this.weatherOnDateChange(updateUI);
      });
      document.addEventListener('mousemove', onDrag);
      document.addEventListener('mouseup', endDrag);
      thumb.addEventListener('touchstart', startDrag);
      document.addEventListener('touchmove', onDrag);
      document.addEventListener('touchend', endDrag);

      // 日期标记点击
      markers.forEach(m => {
        m.addEventListener('click', () => {
          const idx = parseInt(m.dataset.idx);
          this.currentDateIndex = idx;
          this.weatherOnDateChange(updateUI);
        });
      });

      // 播放/暂停
      playBtn.addEventListener('click', () => {
        this.togglePlay(playBtn, updateUI);
      });

      // 存储清理函数
      el._cleanup = () => {
        document.removeEventListener('mousemove', onDrag);
        document.removeEventListener('mouseup', endDrag);
        document.removeEventListener('touchmove', onDrag);
        document.removeEventListener('touchend', endDrag);
        this.stopPlay(playBtn);
      };

      this.timelineEl = el;
    },

    /** 播放/暂停自动推进 */
    togglePlay(playBtn, updateUI) {
      if (this.isPlaying) {
        this.stopPlay(playBtn);
        return;
      }
      this.isPlaying = true;
      playBtn.textContent = '⏸';
      playBtn.classList.add('playing');

      this.playTimer = setInterval(() => {
        if (this.currentDateIndex >= this.totalDays) {
          this.stopPlay(playBtn);
        } else {
          this.currentDateIndex++;
          this.weatherOnDateChange(updateUI);
        }
      }, 1000);
    },

    stopPlay(playBtn) {
      if (this.playTimer) {
        clearInterval(this.playTimer);
        this.playTimer = null;
      }
      this.isPlaying = false;
      if (playBtn) {
        playBtn.textContent = '▶';
        playBtn.classList.remove('playing');
      }
    },

    removeTimeline() {
      this.stopPlay();
      if (this.timelineEl) {
        if (this.timelineEl._cleanup) this.timelineEl._cleanup();
        this.timelineEl.remove();
        this.timelineEl = null;
      }
    },

    /** 日期变更时统一刷新气压、温度、海浪、风力数据 */
    weatherOnDateChange(updateUI) {
      if (typeof updateUI === 'function') updateUI();
      if (this.isPressureActive) {
        this.fetchAndRenderIsoline();
      }
      if (this.weatherLayers.find(l => l.key === 'temperature')?.active) {
        this.updateTemperatureLayer();
      }
      if (this.weatherLayers.find(l => l.key === 'wave')?.active) {
        this.updateWaveLayer();
      }
      if (this.weatherLayers.find(l => l.key === 'wind')?.active) {
        this.updateWindLayer();
      }
    },

    // ========== 图例 ==========

    injectLegendStyles() {
      if (document.getElementById('weather-legend-styles')) return;
      const s = document.createElement('style');
      s.id = 'weather-legend-styles';
      s.textContent =
        '.pressure-legend{position:absolute;bottom:100px;right:20px;background:rgba(0,0,0,.75);color:#fff;border-radius:6px;padding:8px 12px;z-index:50;font-size:11px}' +
        '.pl-inner .pl-title{text-align:center;margin-bottom:4px;font-weight:bold}' +
        '.pl-bar{display:flex;height:14px;align-items:center}' +
        '.pl-seg{flex:1;height:12px;position:relative}' +
        '.pl-seg span{position:absolute;top:16px;left:50%;transform:translateX(-50%);font-size:9px;white-space:nowrap}' +
        '.temperature-legend{position:absolute;bottom:60px;right:20px;background:rgba(0,0,0,.75);color:#fff;border-radius:6px;padding:8px;z-index:50}' +
        '.wave-legend{position:absolute;bottom:60px;right:20px;background:rgba(0,0,0,.75);color:#fff;border-radius:6px;padding:8px;z-index:50}' +
        '.weather-legend{position:absolute;right:12px;background:rgba(0,0,0,.65);color:#fff;border-radius:6px;padding:8px 12px;z-index:50;font-size:12px;pointer-events:none;min-width:180px}' +
        '.weather-legend-pressure{bottom:12px}' +
        '.weather-legend-temperature{bottom:60px}' +
        '.weather-legend-wave{bottom:60px}' +
        '.weather-legend-wind{bottom:186px}' +
        '.weather-legend-title{font-size:12px;margin-bottom:6px;text-align:center}' +
        '.weather-legend-bar{height:10px;border-radius:3px}' +
        '.weather-legend-bar-pressure{background:linear-gradient(to right,#1e90ff,#00ffff,#32cd32,#ffff00,#ff8c00,#ff4500,#dc143c)}' +
        '.weather-legend-bar-temperature{background:linear-gradient(to right,#000080,#1e90ff,#00bfff,#32cd32,#ffff00,#ff8c00,#ff4500,#8b0000)}' +
        '.weather-legend-bar-wave{background:linear-gradient(to right,#1a237e,#1976d2,#03a9f4,#4fc3f7,#b3e5fc,#fff,#ffe082,#ff8f00)}' +
        '.weather-legend-bar-wind{width:16px;height:120px;background:linear-gradient(to top,#2468b4,#3c9dc2,#80cdc1,#97daa8,#c6e7b5,#eef7a7,#ffef9f,#fcd97d,#ffb664,#fc964b,#fa7034,#f54020,#ed2d1c,#dc1820,#b40023)}' +
        '.weather-legend-labels{display:flex;justify-content:space-between;font-size:10px;margin-top:4px}' +
        '.weather-timeline{position:absolute;bottom:84px;left:50%;transform:translateX(-50%);width:500px;background:rgba(0,0,0,.7);border-radius:8px;padding:8px 16px;z-index:99}' +
        '.wtl-header{display:flex;align-items:center;gap:8px;margin-bottom:6px}' +
        '.wtl-play-btn{background:none;border:1px solid #fff;color:#fff;border-radius:4px;padding:2px 8px;cursor:pointer;font-size:14px}' +
        '.wtl-current-label{color:#fff;font-size:13px;font-weight:bold}' +
        '.wtl-slider-container{position:relative;height:6px;margin:8px 0}' +
        '.wtl-slider-track{position:absolute;width:100%;height:100%;background:rgba(255,255,255,.3);border-radius:3px}' +
        '.wtl-slider-fill{position:absolute;height:100%;background:#409EFF;border-radius:3px}' +
        '.wtl-slider-thumb{position:absolute;top:-6px;width:16px;height:16px;background:#fff;border-radius:50%;cursor:pointer;transform:translateX(-50%)}' +
        '.wtl-markers{display:flex;justify-content:space-between;color:rgba(255,255,255,.6);font-size:10px}' +
        '.typhoon-panel{position:absolute;right:100px;top:100px;width:360px;max-height:320px;background:rgba(0,0,0,.72);border-radius:6px;color:#fff;font-size:12px;z-index:100;display:flex;flex-direction:column;overflow:hidden}' +
        '.tp-header{display:flex;justify-content:space-between;align-items:center;padding:8px 12px;font-weight:600;border-bottom:1px solid rgba(255,255,255,.12)}' +
        '.tp-close{cursor:pointer;opacity:.6;padding:0 4px}' +
        '.tp-close:hover{opacity:1}' +
        '.tp-list{flex:1;overflow-y:auto;padding:4px 0}' +
        '.tp-item{padding:8px 12px;cursor:pointer;display:flex;justify-content:space-between;border-bottom:1px solid rgba(255,255,255,.06)}' +
        '.tp-item:hover{background:rgba(255,255,255,.08)}' +
        '.tp-active{background:rgba(24,144,255,.25)}' +
        '.tp-level{color:rgba(255,255,255,.5)}' +
        '.tp-empty{padding:16px;text-align:center;color:rgba(255,255,255,.4)}' +
        '.typhoon-gif-overlay{position:absolute;width:24px;height:24px;transform:translate(-50%,-50%);pointer-events:none;z-index:101}' +
        '.tg-pulse{width:100%;height:100%;border-radius:50%;background:rgba(255,0,0,.6);animation:tg-pulse 1.5s ease-in-out infinite}' +
        '@keyframes tg-pulse{0%,100%{transform:scale(1);opacity:.8}50%{transform:scale(2.5);opacity:0}}';
      document.head.appendChild(s);
    },

    createPressureLegend(data) {
      this.removePressureLegend();
      if (!window.viewer || !data) return;
      const minP = (data.min_pressure / 100).toFixed(0);
      const maxP = (data.max_pressure / 100).toFixed(0);
      const el = document.createElement('div');
      el.className = 'weather-legend weather-legend-pressure';
      el.innerHTML = `
        <div class="weather-legend-title">等压线图例 (hPa)</div>
        <div class="weather-legend-bar weather-legend-bar-pressure"></div>
        <div class="weather-legend-labels">
          <span>${minP}</span>
          <span>${maxP}</span>
        </div>
      `;
      this.injectLegendStyles();
      window.viewer.container.appendChild(el);
      this.pressureLegendEl = el;
    },

    removePressureLegend() {
      if (this.pressureLegendEl?.parentNode) this.pressureLegendEl.parentNode.removeChild(this.pressureLegendEl);
      this.pressureLegendEl = null;
    },

    createTemperatureLegend() {
      this.removeTemperatureLegend();
      if (!window.viewer) return;
      this.injectLegendStyles();
      const el = document.createElement('div');
      el.className = 'temperature-legend';
      el.innerHTML = `
        <div style="font-weight:bold;font-size:11px;text-align:center;margin-bottom:4px;color:#fff;">温度 (℃)</div>
        <div style="display:flex;">
          <div class="tl-bar" style="width:16px;height:140px;background:linear-gradient(to bottom,red,orange,yellow,lime,cyan,blue,white);border-radius:2px;"></div>
          <div style="margin-left:6px;display:flex;flex-direction:column;justify-content:space-between;height:140px;font-size:10px;">
            <span>40</span><span>30</span><span>20</span><span>10</span><span>0</span><span>-10</span><span>-20</span>
          </div>
        </div>`;
      window.viewer.container.appendChild(el);
      this.temperatureLegendEl = el;
    },

    removeTemperatureLegend() {
      if (this.temperatureLegendEl?.parentNode) this.temperatureLegendEl.parentNode.removeChild(this.temperatureLegendEl);
      this.temperatureLegendEl = null;
    },

    createWaveLegend() {
      this.removeWaveLegend();
      if (!window.viewer) return;
      this.injectLegendStyles();
      const el = document.createElement('div');
      el.className = 'wave-legend';
      el.innerHTML = `
        <div style="font-weight:bold;font-size:11px;text-align:center;margin-bottom:4px;color:#fff;">浪高 (m)</div>
        <div style="display:flex;">
          <div style="width:16px;height:100px;background:linear-gradient(to top,#1a5276,#2e86c1,#85c1e9,#aed6f1,#d4e6f1);border-radius:2px;"></div>
          <div style="margin-left:6px;display:flex;flex-direction:column;justify-content:space-between;height:100px;font-size:10px;">
            <span>6</span><span>4</span><span>2</span><span>0.5</span><span>0</span>
          </div>
        </div>`;
      window.viewer.container.appendChild(el);
      this.waveLegendEl = el;
    },

    removeWaveLegend() {
      if (this.waveLegendEl?.parentNode) this.waveLegendEl.parentNode.removeChild(this.waveLegendEl);
      this.waveLegendEl = null;
    },

    createWindLegend() {
      this.removeWindLegend();
      if (!window.viewer) return;
      this.injectLegendStyles();
      const el = document.createElement('div');
      el.className = 'weather-legend weather-legend-wind';
      el.innerHTML = `
        <div class="weather-legend-title">风力图例 (m/s)</div>
        <div class="weather-legend-bar weather-legend-bar-wind"></div>
        <div class="weather-legend-labels"><span>25</span><span>0</span></div>`;
      window.viewer.container.appendChild(el);
      this.windLegendEl = el;
    },

    removeWindLegend() {
      if (this.windLegendEl?.parentNode) this.windLegendEl.parentNode.removeChild(this.windLegendEl);
      this.windLegendEl = null;
    },

    // ========== 温度瓦片图层 ==========

    updateTemperatureLayer() {
      if (!window.viewer) return;
      const oldLayer = this.temperatureLayer;
      const timestamp = this.getTodayTimestamp();
      const url = `${PRESSURE_API_BASE}?appkey=${PRESSURE_APPKEY}&cmd=gettile&x={x}&y={y}&z={z}&type=2t&timestamp=${timestamp}`;
      const provider = new Cesium.UrlTemplateImageryProvider({ url });
      const layer = window.viewer.imageryLayers.addImageryProvider(provider);
      this.temperatureLayer = layer;
      if (oldLayer) {
        window.viewer.imageryLayers.remove(oldLayer, true);
      }
      this.createTemperatureLegend();
    },

    removeTemperatureLayer() {
      if (this.temperatureLayer && window.viewer) {
        window.viewer.imageryLayers.remove(this.temperatureLayer, true);
      }
      this.temperatureLayer = null;
    },

    // ========== 海浪瓦片图层 ==========

    updateWaveLayer() {
      if (!window.viewer) return;
      const oldLayer = this.waveLayer;
      const timestamp = this.getTodayTimestamp();
      const url = `${PRESSURE_API_BASE}?appkey=${PRESSURE_APPKEY}&cmd=gettile&x={x}&y={y}&z={z}&type=swh&timestamp=${timestamp}`;
      const provider = new Cesium.UrlTemplateImageryProvider({ url });
      const layer = window.viewer.imageryLayers.addImageryProvider(provider);
      this.waveLayer = layer;
      if (oldLayer) {
        window.viewer.imageryLayers.remove(oldLayer, true);
      }
      this.createWaveLegend();
    },

    removeWaveLayer() {
      if (this.waveLayer && window.viewer) {
        window.viewer.imageryLayers.remove(this.waveLayer, true);
      }
      this.waveLayer = null;
    },

    // ========== 台风 ==========

    createTyphoonPanel() {
      this.injectTyphoonPanelStyles();
      if (this.typhoonPanelEl) return;
      const el = document.createElement('div');
      el.className = 'typhoon-panel';
      el.innerHTML = `
        <div class="tp-header">
          <span>台风列表</span>
          <span class="tp-refresh" title="刷新">↻</span>
          <span class="tp-close">✕</span>
        </div>
        <div class="tp-head-row">
          <span class="tp-dot-head"></span>
          <span class="tp-name">名称</span>
          <span class="tp-en">英文</span>
          <span class="tp-no">编号</span>
          <span class="tp-state">状态</span>
          <span class="tp-sim-head">操作</span>
        </div>
        <div class="tp-body"></div>`;
      window.viewer.container.appendChild(el);
      el.querySelector('.tp-close').addEventListener('click', () => {
        const layer = this.weatherLayers.find(l => l.key === 'typhoon');
        if (layer) layer.active = false;
        this.removeTyphoonPanel();
        this.typhoonList = [];
        this.selectedTyphoon = null;
      });
      el.querySelector('.tp-refresh').addEventListener('click', () => {
        this.fetchTyphoonList();
      });
      this.typhoonPanelEl = el;
    },

    injectTyphoonPanelStyles() {
      if (document.getElementById('typhoon-panel-style')) return;
      const style = document.createElement('style');
      style.id = 'typhoon-panel-style';
      style.textContent = `
        .typhoon-panel {
          position: absolute;
          right: 80px;
          top: 100px;
          width: 360px;
          max-height: 320px;
          background: rgba(0, 0, 0, 0.72);
          border-radius: 6px;
          color: #fff;
          font-size: 12px;
          z-index: 100;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .tp-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 12px;
          font-weight: 600;
          border-bottom: 1px solid rgba(255,255,255,0.12);
          flex-shrink: 0;
        }
        .tp-close, .tp-refresh { cursor: pointer; opacity: 0.6; padding: 0 4px; }
        .tp-close:hover, .tp-refresh:hover { opacity: 1; }
        .tp-head-row {
          display: flex;
          align-items: center;
          padding: 4px 12px;
          font-size: 10px;
          color: rgba(255,255,255,0.45);
          border-bottom: 1px solid rgba(255,255,255,0.1);
          flex-shrink: 0;
        }
        .tp-head-row span { display: inline-block; }
        .tp-head-row .tp-dot-head { width: 6px; margin-right: 8px; flex-shrink: 0; }
        .tp-head-row .tp-sim-head { margin-left: 8px; width: 64px; text-align: center; }
        .tp-body {
          flex: 1;
          overflow-y: auto;
          padding: 4px 0;
        }
        .tp-body::-webkit-scrollbar { width: 4px; }
        .tp-body::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 2px; }
        .tp-loading, .tp-empty { text-align: center; padding: 20px 0; opacity: 0.6; }
        .tp-item {
          display: flex;
          align-items: center;
          padding: 6px 12px;
          cursor: pointer;
          transition: background 0.15s;
        }
        .tp-item:hover { background: rgba(255,255,255,0.1); }
        .tp-item.selected { background: rgba(24,144,255,0.25); }
        .tp-dot {
          width: 6px; height: 6px;
          border-radius: 50%;
          margin-right: 8px;
          flex-shrink: 0;
          background: #999;
        }
        .tp-dot.active { background: #e74c3c; }
        .tp-name { font-weight: 500; width: 60px; flex-shrink: 0; }
        .tp-en { opacity: 0.5; margin-left: 6px; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .tp-no { opacity: 0.6; font-size: 11px; width: 45px; text-align: center; }
        .tp-state {
          display: inline-block;
          font-size: 10px;
          padding: 1px 0;
          width: 45px;
          text-align: center;
          border-radius: 8px;
          background: rgba(255,255,255,0.12);
          white-space: nowrap;
          box-sizing: border-box;
        }
        .tp-sim-btn {
          margin-left: 8px;
          padding: 1px 0;
          width: 64px;
          font-size: 11px;
          border: 1px solid rgba(255,255,255,0.3);
          border-radius: 4px;
          background: rgba(255,255,255,0.08);
          color: #ccc;
          cursor: pointer;
          white-space: nowrap;
          transition: all .2s;
          box-sizing: border-box;
        }
        .tp-sim-btn:hover { border-color: #1890ff; color: #1890ff; }
        .tp-sim-btn.running { border-color: #e74c3c; color: #e74c3c; background: rgba(231,76,60,0.15); }
      `;
      document.head.appendChild(style);
    },

    refreshTyphoonPanel() {
      if (!this.typhoonPanelEl) return;
      const bodyEl = this.typhoonPanelEl.querySelector('.tp-body');
      if (!bodyEl) return;
      if (this.typhoonLoading) {
        bodyEl.innerHTML = '<div class="tp-loading">加载台风数据...</div>';
        return;
      }
      if (!this.typhoonList.length) {
        bodyEl.innerHTML = '<div class="tp-empty">暂无台风数据</div>';
        return;
      }
      bodyEl.innerHTML = this.typhoonList.map((t, i) => {
        const selected = this.selectedTyphoon && this.selectedTyphoon.id === t.id ? ' selected' : '';
        const dotClass = t.isActive ? 'tp-dot active' : 'tp-dot';
        const stateText = t.isActive ? '活跃' : '历史';
        return `<div class="tp-item${selected}" data-idx="${i}">
          <span class="${dotClass}"></span>
          <span class="tp-name">${t.cnName}</span>
          <span class="tp-en">${t.enName || '-'}</span>
          <span class="tp-no">${t.no || '-'}</span>
          <span class="tp-state">${stateText}</span>
          <button class="tp-sim-btn">轨迹模拟</button>
        </div>`;
      }).join('');
      bodyEl.querySelectorAll('.tp-item').forEach(el => {
        el.addEventListener('click', (e) => {
          if (e.target.classList.contains('tp-sim-btn')) {
            e.stopPropagation();
            const idx = parseInt(el.dataset.idx);
            this.toggleTrackSimulation(this.typhoonList[idx]);
            return;
          }
          const idx = parseInt(el.dataset.idx);
          this.stopTrackSimulation();
          this.selectTyphoon(this.typhoonList[idx]);
          this.refreshTyphoonPanel();
        });
      });
      this.updateSimBtnState(bodyEl);
    },

    removeTyphoonPanel() {
      this.stopTrackSimulation();
      this.clearTyphoonTrack();
      if (this.typhoonPanelEl) {
        this.typhoonPanelEl.remove();
        this.typhoonPanelEl = null;
      }
    },

    fetchTyphoonList() {
      this.typhoonLoading = true;
      this.refreshTyphoonPanel();
      const callbackName = 'typhoon_jsons_list_default';
      const timestamp = Date.now();
      const url = `http://typhoon.nmc.cn/weatherservice/typhoon/jsons/list_default?t=${timestamp}&callback=${callbackName}`;
      // 移除上一次的 script（如果还在加载中）
      if (this._typhoonScript) {
        this._typhoonScript.remove();
      }
      const script = document.createElement('script');
      this._typhoonScript = script;
      const cleanup = () => {
        if (window[callbackName]) delete window[callbackName];
        if (script.parentNode) script.parentNode.removeChild(script);
        if (this._typhoonScript === script) this._typhoonScript = null;
      };
      window[callbackName] = (data) => {
        cleanup();
        this.typhoonLoading = false;
        if (data && Array.isArray(data.typhoonList)) {
          this.typhoonList = data.typhoonList.map(t => ({
            id: t[0], enName: t[1], cnName: t[2],
            no: t[3], backupNo: t[4], extId: t[5],
            desc: t[6], isActive: t[7] === 'start',
          }));
        } else {
          this.typhoonList = [];
        }
        this.refreshTyphoonPanel();
      };
      script.src = url;
      script.onerror = () => {
        cleanup();
        this.typhoonLoading = false;
        this.typhoonList = [];
        this.refreshTyphoonPanel();
      };
      document.head.appendChild(script);
    },

    selectTyphoon(t) {
      this.stopTrackSimulation();
      this.clearTyphoonTrack();
      this.selectedTyphoon = t;
      this.refreshTyphoonPanel();
      this.fetchTyphoonTrack(t.id);
    },

    fetchTyphoonTrack(typhoonId) {
      const callbackName = `typhoon_jsons_view_${typhoonId}`;
      const timestamp = Date.now();
      const url = `http://typhoon.nmc.cn/weatherservice/typhoon/jsons/view_${typhoonId}?t=${timestamp}&callback=${callbackName}`;
      const script = document.createElement('script');
      const cleanup = () => {
        if (window[callbackName]) delete window[callbackName];
        if (script.parentNode) script.parentNode.removeChild(script);
      };
      window[callbackName] = (data) => {
        cleanup();
        if (data && Array.isArray(data.typhoon)) {
          // 动态查找轨迹点数组（第一个元素也是数组的即为路径点集合）
          let trackArr = null;
          for (let i = 0; i < data.typhoon.length; i++) {
            const item = data.typhoon[i];
            if (Array.isArray(item) && item.length > 0 && Array.isArray(item[0])) {
              trackArr = item;
              break;
            }
          }
          if (trackArr) {
            this.typhoonTrackData = trackArr
              .filter(point => Array.isArray(point) && point.length >= 8)
              .map(point => ({
                id: point[0], time: point[1], level: point[3],
                lng: point[4], lat: point[5], pressure: point[6], wind: point[7],
              }));
            if (this.selectedTyphoon) {
              this.selectedTyphoon.trackUrl = data.typhoon[2] || null;
            }
            this.renderTyphoonTrack();
          } else {
            this.typhoonTrackData = [];
          }
        } else {
          this.typhoonTrackData = [];
        }
      };
      script.src = url;
      script.onerror = () => {
        cleanup();
        this.typhoonTrackData = [];
      };
      document.head.appendChild(script);
    },

    renderTyphoonTrack() {
      if (!window.viewer || this.typhoonTrackData.length < 2) return;
      this.clearTyphoonTrack(true);
      const levelNames = {
        TD: '热带低压', TS: '热带风暴', STS: '强热带风暴',
        TY: '台风', STY: '强台风', SuperTY: '超强台风',
        '': '未知', undefined: '未知',
      };
      const colorMap = {
        TD: [79, 195, 247], TS: [50, 205, 50], STS: [255, 255, 0],
        TY: [255, 140, 0], STY: [255, 69, 0], SuperTY: [220, 20, 60],
      };
      const getColor = (level) => {
        const c = colorMap[level] || [128, 128, 128];
        return new Cesium.Color(c[0] / 255, c[1] / 255, c[2] / 255, 0.92);
      };
      const fmtTime = (t) => {
        if (!t) return '-';
        if (/^\d{10,}$/.test(t)) return `${t.slice(0, 4)}-${t.slice(4, 6)}-${t.slice(6, 8)} ${t.slice(8, 10)}:${t.slice(10, 12) || '00'}`;
        return t;
      };
      const entities = [];
      for (let i = 0; i < this.typhoonTrackData.length - 1; i++) {
        const p0 = this.typhoonTrackData[i];
        const p1 = this.typhoonTrackData[i + 1];
        const lineEntity = window.viewer.entities.add({
          polyline: {
            positions: Cesium.Cartesian3.fromDegreesArray([p0.lng, p0.lat, p1.lng, p1.lat]),
            width: 2.8,
            material: getColor(p0.level),
          },
        });
        entities.push(lineEntity);
      }
      // 关键节点（终点用 HTML 叠加层实现 GIF 动画，不创建 entity）
      this.typhoonTrackData.forEach((point, idx) => {
        const isFirst = idx === 0;
        const isLast = idx === this.typhoonTrackData.length - 1;
        if (isLast) {
          // 终点使用 HTML GIF 叠加层，不创建 entity
          return;
        }
        try {
          const entity = window.viewer.entities.add({
            position: Cesium.Cartesian3.fromDegrees(point.lng, point.lat),
            point: {
              pixelSize: isFirst ? 10 : 9,
              color: getColor(point.level),
              outlineColor: Cesium.Color.WHITE,
              outlineWidth: 2,
            },
            description: (() => {
              return `\
                <table style="color:#fff;font-size:13px;border-collapse:collapse;">
                  <tr><td style="padding:2px 8px 2px 0;opacity:0.6;">时间</td><td style="padding:2px 0;">${fmtTime(point.time)}</td></tr>
                  <tr><td style="padding:2px 8px 2px 0;opacity:0.6;">强度</td><td style="padding:2px 0;">${levelNames[point.level] || point.level || '-'}</td></tr>
                  <tr><td style="padding:2px 8px 2px 0;opacity:0.6;">经度</td><td style="padding:2px 0;">${point.lng?.toFixed(2) || '-'}°</td></tr>
                  <tr><td style="padding:2px 8px 2px 0;opacity:0.6;">纬度</td><td style="padding:2px 0;">${point.lat?.toFixed(2) || '-'}°</td></tr>
                  <tr><td style="padding:2px 8px 2px 0;opacity:0.6;">气压</td><td style="padding:2px 0;">${point.pressure != null ? point.pressure + ' hPa' : '-'}</td></tr>
                  <tr><td style="padding:2px 8px 2px 0;opacity:0.6;">风速</td><td style="padding:2px 0;">${point.wind != null ? point.wind + ' m/s' : '-'}</td></tr>
                </table>`;
            })(),
          });
          entities.push(entity);
        } catch (e) {
          // 跳过创建失败的节点
        }
      });
      this.typhoonTrackEntities = entities;
      if (!this.typhoonGifOverlay) {
        const lastPoint = this.typhoonTrackData[this.typhoonTrackData.length - 1];
        if (lastPoint) this.createTyphoonGifOverlay(lastPoint);
      }
      this.$nextTick(() => this.bindTyphoonTrackClick());
    },

    clearTyphoonTrack(keepData = false) {
      this.unbindTyphoonTrackClick();
      this.stopTrackSimulation();
      this.trackSimulation = null;
      if (window.viewer && this.typhoonTrackEntities.length) {
        this.typhoonTrackEntities.forEach(e => { try { window.viewer.entities.remove(e); } catch (_) {} });
      }
      this.typhoonTrackEntities = [];
      if (!keepData) this.typhoonTrackData = [];
      this.removeTyphoonGifOverlay();
    },

    bindTyphoonTrackClick() {
      this.unbindTyphoonTrackClick();
      if (!window.viewer || !this.typhoonTrackEntities.length) return;
      const viewer = window.viewer;
      const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
      handler.setInputAction((click) => {
        const picked = viewer.scene.pick(click.position);
        if (picked && picked.id && picked.id.description) {
          const desc = typeof picked.id.description.getValue === 'function'
            ? picked.id.description.getValue()
            : picked.id.description;
          this.typhoonNodePopup = {
            show: true,
            x: click.position.x + 10,
            y: click.position.y - 10,
            html: typeof desc === 'string' ? desc : String(desc),
          };
        } else {
          this.typhoonNodePopup.show = false;
        }
      }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
      this.typhoonClickHandler = handler;
    },

    unbindTyphoonTrackClick() {
      if (this.typhoonClickHandler) {
        this.typhoonClickHandler.destroy();
        this.typhoonClickHandler = null;
      }
      this.typhoonNodePopup.show = false;
    },

    createTyphoonGifOverlay(lastPoint) {
      if (!lastPoint || !window.viewer) return;
      this.removeTyphoonGifOverlay();
      const el = document.createElement('div');
      el.className = 'typhoon-gif-overlay';
      el.innerHTML = `<img src="data/img/typhoon.gif" width="48" height="48" style="display:block;" />`;
      Object.assign(el.style, {
        position: 'absolute',
        pointerEvents: 'none',
        zIndex: '100',
        transform: 'translate(-50%, -50%)',
        display: 'none',
      });
      window.viewer.container.appendChild(el);
      // 使用可变引用，支持模拟时动态更新位置
      const positionRef = { value: Cesium.Cartesian3.fromDegrees(lastPoint.lng, lastPoint.lat) };
      const listener = () => {
        const canvasPos = window.viewer.scene.cartesianToCanvasCoordinates(positionRef.value, new Cesium.Cartesian2());
        if (Cesium.defined(canvasPos)) {
          el.style.left = canvasPos.x + 'px';
          el.style.top = canvasPos.y + 'px';
          el.style.display = '';
        } else {
          el.style.display = 'none';
        }
      };
      window.viewer.scene.postRender.addEventListener(listener);
      this.typhoonGifOverlay = { el, positionRef, listener };
    },

    removeTyphoonGifOverlay() {
      if (this.typhoonGifOverlay) {
        const { el, listener } = this.typhoonGifOverlay;
        if (window.viewer && listener) {
          window.viewer.scene.postRender.removeEventListener(listener);
        }
        if (el && el.parentNode) {
          el.parentNode.removeChild(el);
        }
        this.typhoonGifOverlay = null;
      }
    },

    // ========== 轨迹模拟 ==========

    toggleTrackSimulation(typhoon) {
      if (this.trackSimulation && this.trackSimulation.typhoonId === typhoon.id) {
        this.stopTrackSimulation();
        this.refreshTyphoonPanel();
        return;
      }
      this.startTrackSimulation(typhoon);
    },

    async startTrackSimulation(typhoon) {
      this.stopTrackSimulation();
      let trackData;
      if (this.selectedTyphoon && this.selectedTyphoon.id === typhoon.id && this.typhoonTrackData.length) {
        trackData = this.typhoonTrackData;
      } else {
        this.selectTyphoon(typhoon);
        trackData = await this.fetchTyphoonTrackData(typhoon.id);
      }
      if (!trackData || trackData.length < 2) {
        console.warn('无可用于模拟的轨迹数据');
        this.refreshTyphoonPanel();
        return;
      }
      this.createTyphoonGifOverlay(trackData[trackData.length - 1]);
      const sim = {
        typhoonId: typhoon.id,
        currentIdx: 0,
        trackData,
        timer: null,
      };
      const interval = 250;
      sim.timer = setInterval(() => {
        sim.currentIdx++;
        if (sim.currentIdx >= trackData.length) {
          this.stopTrackSimulation();
          this.refreshTyphoonPanel();
          return;
        }
        const p = trackData[sim.currentIdx];
        if (this.typhoonGifOverlay && this.typhoonGifOverlay.positionRef) {
          this.typhoonGifOverlay.positionRef.value = Cesium.Cartesian3.fromDegrees(p.lng, p.lat);
        }
      }, interval);
      this.trackSimulation = sim;
      this.refreshTyphoonPanel();
    },

    stopTrackSimulation() {
      if (this.trackSimulation) {
        if (this.trackSimulation.timer) {
          clearInterval(this.trackSimulation.timer);
        }
        this.trackSimulation = null;
      }
    },

    updateSimBtnState(body) {
      if (!this.trackSimulation) return;
      const runningId = this.trackSimulation.typhoonId;
      body.querySelectorAll('.tp-sim-btn').forEach(btn => {
        const idx = parseInt(btn.parentElement.dataset.idx);
        const t = this.typhoonList[idx];
        if (t && t.id === runningId) {
          btn.textContent = '停止模拟';
          btn.classList.add('running');
        }
      });
    },

    fetchTyphoonTrackData(typhoonId) {
      return new Promise((resolve) => {
        const typhoon = this.typhoonList.find(t => t.id === typhoonId);
        if (!typhoon || !typhoon.trackUrl) { resolve(null); return; }
        const callbackName = 'typhoon_sim_jsonp_' + Date.now();
        if (window[callbackName]) delete window[callbackName];
        const script = document.createElement('script');
        let done = false;
        const cleanup = () => {
          if (done) return;
          done = true;
          delete window[callbackName];
          script.remove();
        };
        window[callbackName] = (data) => {
          cleanup();
          if (data && data.typhoon && Array.isArray(data.typhoon)) {
            let trackArr = null;
            for (let i = 0; i < data.typhoon.length; i++) {
              const item = data.typhoon[i];
              if (Array.isArray(item) && item.length > 0 && Array.isArray(item[0])) {
                trackArr = item;
                break;
              }
            }
            if (trackArr) {
              const result = trackArr
                .filter(point => Array.isArray(point) && point.length >= 8)
                .map(point => ({
                  id: point[0], time: point[1], level: point[3],
                  lng: point[4], lat: point[5], pressure: point[6], wind: point[7],
                }));
              resolve(result);
            } else { resolve(null); }
          } else { resolve(null); }
        };
        script.src = typhoon.trackUrl + '?callback=' + callbackName;
        script.onerror = () => { cleanup(); resolve(null); };
        document.head.appendChild(script);
      });
    },

    // ========== 潮汐 ==========

    loadTideData() {
      if (this.tidePoints.length > 0) { this.renderTidePoints(); return; }
      const url = 'http://www.sailxy.com/js/map/tideporthf.js';
      if (this._tideScript) this._tideScript.remove();
      const script = document.createElement('script');
      this._tideScript = script;
      const orig = window.tideporthf;
      delete window.tideporthf;
      script.onload = () => {
        const data = window.tideporthf;
        window.tideporthf = orig;
        if (script.parentNode) script.parentNode.removeChild(script);
        this._tideScript = null;
        if (data && Array.isArray(data)) { this.tidePoints = data; this.renderTidePoints(data); }
      };
      script.onerror = () => { window.tideporthf = orig; if (script.parentNode) script.parentNode.removeChild(script); this._tideScript = null; };
      script.src = url;
      document.head.appendChild(script);
    },

    renderTidePoints(data) {
      if (!window.viewer) return;
      this.removeTidePoints();
      const points = data || this.tidePoints;
      const entities = [];
      points.forEach(station => {
        const lat = parseFloat(station.lat), lon = parseFloat(station.lon);
        if (isNaN(lat) || isNaN(lon)) return;
        entities.push(window.viewer.entities.add({
          position: Cesium.Cartesian3.fromDegrees(lon, lat),
          label: {
            text: station.zhname, font: '12px sans-serif', fillColor: Cesium.Color.CYAN,
            outlineColor: Cesium.Color.BLACK, outlineWidth: 2, style: Cesium.LabelStyle.FILL_AND_OUTLINE,
            verticalOrigin: Cesium.VerticalOrigin.TOP, pixelOffset: new Cesium.Cartesian2(0, 12),
            distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, 3000000),
          },
          point: { pixelSize: 6, color: Cesium.Color.CYAN, outlineColor: Cesium.Color.DARKBLUE, outlineWidth: 2 },
          properties: { zhname: station.zhname, portid: station.portid },
        }));
      });
      this.tideEntities = entities;
      this.$nextTick(() => this.bindTideClick());
    },

    removeTidePoints() {
      if (window.viewer && this.tideEntities.length) {
        this.tideEntities.forEach(e => { try { window.viewer.entities.remove(e); } catch (_) {} });
      }
      this.tideEntities = [];
      this.unbindTideClick();
      this.closeTideDetail();
    },

    bindTideClick() {
      this.unbindTideClick();
      if (!window.viewer || !this.tideEntities.length) return;
      const handler = new Cesium.ScreenSpaceEventHandler(window.viewer.scene.canvas);
      handler.setInputAction((click) => {
        const picked = window.viewer.scene.pick(click.position);
        if (picked && picked.id && picked.id.properties) {
          const portid = picked.id.properties.portid?.getValue();
          const zhname = picked.id.properties.zhname?.getValue();
          if (portid) this.openTideDetail(portid, zhname);
        }
      }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
      this.tideClickHandler = handler;
    },

    unbindTideClick() {
      if (this.tideClickHandler) { this.tideClickHandler.destroy(); this.tideClickHandler = null; }
    },

    openTideDetail(portid, zhname) {
      this.tideDetailPopup = {
        show: true,
        loading: true,
        data: null,
        portid: portid,
      };
      this.fetchTideDetail(portid, zhname);
    },

    closeTideDetail() {
      this.tideDetailPopup.show = false;
      this.tideDetailPopup.data = null;
      if (this.tideChartInstance) {
        this.tideChartInstance.dispose();
        this.tideChartInstance = null;
      }
    },

    formatToday() {
      const d = new Date();
      const pad = (n) => (n < 10 ? '0' + n : n);
      return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
    },

    fetchTideDetail(portid, zhname) {
      const callbackName = 'tideDetailCallback_' + Date.now();
      const url = 'http://112.126.96.159:9091/' + '?appkey=' + PRESSURE_APPKEY + '&cmd=gettide&id=' + portid;
      const script = document.createElement('script');
      script.src = url + '&callback=' + callbackName;
      window[callbackName] = (data) => {
        delete window[callbackName];
        if (script.parentNode) script.parentNode.removeChild(script);
        this.handleTideData(portid, zhname, data);
      };
      script.onerror = () => {
        delete window[callbackName];
        if (script.parentNode) script.parentNode.removeChild(script);
        this.fetchTideDetailDirect(portid, zhname);
      };
      document.head.appendChild(script);
    },

    fetchTideDetailDirect(portid, zhname) {
      const url = 'http://112.126.96.159:9091/' + '?appkey=' + PRESSURE_APPKEY + '&cmd=gettide&id=' + portid;
      fetch(url).then(res => res.json()).then(data => {
        this.handleTideData(portid, zhname, data);
      }).catch(() => {
        this.tideDetailPopup.loading = false;
        this.tideDetailPopup.data = { zhname: zhname, tideList: [], hourlyList: [] };
      });
    },

    handleTideData(_portid, zhname, raw) {
      let tideTable = [];
      let tideHourly = [];
      let date = '';

      if (raw) {
        tideTable = raw.tideTable || [];
        tideHourly = raw.tideHourly || [];
        const firstItem = tideHourly[0] || tideTable[0];
        if (firstItem && firstItem.fxTime) {
          const match = firstItem.fxTime.match(/^(\d{4}-\d{2}-\d{2})/);
          if (match) date = match[1];
        }
      }

      this.tideDetailPopup.loading = false;
      this.tideDetailPopup.data = {
        zhname: zhname,
        date: date,
        tideTable: tideTable,
        tideHourly: tideHourly,
      };

      this.$nextTick(() => {
        if (tideHourly && tideHourly.length) {
          this.renderTideChart(tideHourly);
        }
      });
    },

    renderTideChart(tideHourly) {
      const container = this.$refs.tideChart;
      if (!container) return;
      const echartsLib = require('echarts');
      if (this.tideChartInstance) {
        this.tideChartInstance.dispose();
      }
      const chart = echartsLib.init(container);
      this.tideChartInstance = chart;

      const xData = tideHourly.map(i => this.formatTideTime(i.fxTime));
      const yData = tideHourly.map(i => parseFloat(i.height));
      const markData = this.tideDetailPopup.data.tideTable
        ? this.tideDetailPopup.data.tideTable.map(i => ({
            xAxis: this.formatTideTime(i.fxTime),
            yAxis: parseFloat(i.height),
            label: { formatter: (i.type === 'H' ? ' 高潮' : ' 低潮') + '\n' + i.height + 'm' },
          }))
        : [];

      chart.setOption({
        grid: { top: 30, right: 20, bottom: 30, left: 50 },
        xAxis: { type: 'category', data: xData, axisLabel: { color: '#666', fontSize: 11 } },
        yAxis: { type: 'value', name: '潮高(m)', axisLabel: { color: '#666', fontSize: 11 } },
        tooltip: { trigger: 'axis', formatter: (p) => '潮时: ' + p[0].axisValue + '<br/>潮高: ' + p[0].value + 'm' },
        series: [{
          type: 'line',
          data: yData,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: { color: '#1e90ff' },
          lineStyle: { width: 2, color: '#1e90ff' },
          areaStyle: {
            color: {
              type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [{ offset: 0, color: 'rgba(30,144,255,0.3)' }, { offset: 1, color: 'rgba(30,144,255,0.05)' }],
            },
          },
          markPoint: {
            data: markData,
            symbol: 'pin',
            symbolSize: 36,
            itemStyle: { color: '#ff5722' },
          },
        }],
      });
    },

    formatTideTime(fxTime) {
      if (!fxTime) return '-';
      const match = fxTime.match(/T(\d{2}:\d{2})/);
      return match ? match[1] : fxTime;
    },

    // ========== 风力粒子图层 ==========

    updateWindLayer() {
      if (!window.viewer || this.windLoading) return;
      this.windLoading = true;
      const timestamp = this.getTodayTimestamp();
      const url = PRESSURE_API_BASE + `?appkey=${PRESSURE_APPKEY}&cmd=getwindjson&timestamp=${timestamp}`;
      fetch(url).then(res => res.json()).then(data => {
        if (!Array.isArray(data) || data.length < 2) { this.windLoading = false; return; }
        this.handleWindData(data);
      }).catch(() => { this.windLoading = false; });
    },

    handleWindData(rawData) {
      this.windLoading = false;
      if (!rawData || rawData.length < 2) return;
      const uData = rawData[0], vData = rawData[1];
      if (!uData?.header || !vData?.header || !uData.data || !vData.data) return;
      const windData = {
        bounds: { west: uData.header.lo1 || -180, east: uData.header.lo2 || 179, south: uData.header.la2 || -90, north: uData.header.la1 || 90 },
        width: uData.header.nx || 360, height: uData.header.ny || 181,
        u: { array: new Float32Array(uData.data) }, v: { array: new Float32Array(vData.data) },
      };
      if (typeof CesiumWindLayer === 'undefined' || !CesiumWindLayer.WindLayer) return;
      this.removeWindLayer();
      try {
        this.windLayer = new CesiumWindLayer.WindLayer(window.viewer, windData, {
          ...CesiumWindLayer.WindLayer.defaultOptions,
          particlesTextureSize: 1024, flipY: true, useViewerBounds: true, dynamic: true,
          fadeOpacity: 0.96, maxParticles: 2048, speedFactor: 3.0,
          lineWidth: { min: 1, max: 3 }, lineLength: { min: 200, max: 600 },
          particleHeight: 200, domain: { min: 0, max: 25 },
          colors: ['rgb(36,104,180)','rgb(60,157,194)','rgb(128,205,193)','rgb(151,218,168)','rgb(238,247,167)','rgb(255,238,159)','rgb(252,150,75)','rgb(245,64,32)','rgb(180,0,35)'],
        });
        this.createWindLegend();
      } catch (e) { console.error('WindLayer创建失败:', e); }
    },

    removeWindLayer() {
      if (this.windLayer) {
        try { if (typeof this.windLayer.destroy === 'function') this.windLayer.destroy(); else if (typeof this.windLayer.remove === 'function') this.windLayer.remove(); } catch (e) { }
        this.windLayer = null;
      }
      this.windLoading = false;
    },

    // ========== 天气预报 ==========

    /** 打开天气预报面板 */
    openForecastPanel() {
      // 获取地图中心点作为默认位置
      if (window.viewer) {
        const canvas = window.viewer.canvas;
        const center = window.viewer.camera.pickEllipsoid(
          new Cesium.Cartesian2(canvas.clientWidth / 2, canvas.clientHeight / 2)
        );
        if (Cesium.defined(center)) {
          const cartographic = Cesium.Cartographic.fromCartesian(center);
          const lng = Cesium.Math.toDegrees(cartographic.longitude);
          const lat = Cesium.Math.toDegrees(cartographic.latitude);
          this.forecastPanel.position = { lng, lat };
        }
      }
      this.forecastPanel.show = true;
      this.forecastPanel.loading = true;
      this.addForecastEntity();
      this.loadForecastCities();
      this.loadForecastData().then(() => {
        this.forecastPanel.loading = false;
      }).catch(() => {
        this.forecastPanel.loading = false;
      });
    },

    /** 加载省市区数据 */
    loadForecastCities() {
      if (this.forecastCityOptions.length > 0) return;
      try {
        axios.get('/forecastCities.json').then(res => {
          this.forecastCityOptions = res.data || [];
        }).catch(() => {
          console.warn('省市区数据加载失败');
        });
      } catch (e) {
        console.warn('省市区数据加载失败:', e);
      }
    },

    /** 关闭天气预报面板 */
    closeForecastPanel() {
      this.forecastPanel.show = false;
      this.removeForecastEntity();
      this.destroyForecastClickHandler();
      this.forecastClickMode = false;
    },

    /** 切换点选模式 */
    toggleForecastPickMode() {
      this.forecastClickMode = !this.forecastClickMode;
      if (this.forecastClickMode) {
        this.initForecastClickHandler();
      } else {
        this.destroyForecastClickHandler();
      }
    },

    /** 初始化地图点击选点 */
    initForecastClickHandler() {
      if (!window.viewer) return;
      this.destroyForecastClickHandler();
      try {
        this.forecastClickHandler = new Cesium.ScreenSpaceEventHandler(window.viewer.canvas);
        this.forecastClickHandler.setInputAction((click) => {
          const picked = window.viewer.scene.pick(click.position);
          let position;
          if (Cesium.defined(picked)) {
            const cartesian = window.viewer.scene.pickPosition(click.position);
            if (Cesium.defined(cartesian)) {
              position = cartesian;
            }
          }
          if (!position) {
            position = window.viewer.camera.pickEllipsoid(click.position);
          }
          if (Cesium.defined(position)) {
            const cartographic = Cesium.Cartographic.fromCartesian(position);
            const lng = Cesium.Math.toDegrees(cartographic.longitude);
            const lat = Cesium.Math.toDegrees(cartographic.latitude);
            this.moveForecastToPosition(lng, lat);
            // 点选完成后退出点选模式
            this.forecastClickMode = false;
            this.destroyForecastClickHandler();
          }
        }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
      } catch (e) {
        console.warn('预报点选初始化失败:', e);
      }
    },

    /** 销毁地图点击监听 */
    destroyForecastClickHandler() {
      if (this.forecastClickHandler) {
        this.forecastClickHandler.destroy();
        this.forecastClickHandler = null;
      }
    },

    /** 位置变更：移动实体并重新加载预报数据 */
    moveForecastToPosition(lng, lat) {
      this.forecastPanel.position = { lng, lat };
      // 更新实体位置
      if (this.forecastEntity && window.viewer) {
        this.forecastEntity.position = Cesium.Cartesian3.fromDegrees(lng, lat);
      } else {
        this.addForecastEntity();
      }
      // 重新加载数据
      this.forecastPanel.loading = true;
      this.loadForecastData().then(() => {
        this.forecastPanel.loading = false;
      }).catch(() => {
        this.forecastPanel.loading = false;
      });
    },

    /** 省市区选择 */
    onForecastCityChange(val) {
      if (!val || !val.length) return;
      // 最后一级是城市节点
      const provinceVal = val[0];
      const province = this.forecastCityOptions.find(p => p.value === provinceVal);
      if (!province) return;
      let city;
      if (val.length >= 2) {
        city = province.children.find(c => c.value === val[1]);
      } else {
        // 直辖市，自己就是城市
        city = province;
      }
      if (city && city.lng != null && city.lat != null) {
        this.moveForecastToPosition(city.lng, city.lat);
        // 飞行到该位置
        if (window.viewer) {
          window.viewer.flyTo(this.forecastEntity, { duration: 1, offset: new Cesium.HeadingPitchRange(0, Cesium.Math.toRadians(-60), 500000) });
        }
      }
    },

    /** 添加预报位置实体 */
    addForecastEntity() {
      this.removeForecastEntity();
      if (!window.viewer) return;
      const { lng, lat } = this.forecastPanel.position;
      try {
        this.forecastEntity = window.viewer.entities.add({
          position: Cesium.Cartesian3.fromDegrees(lng, lat),
          point: {
            pixelSize: 8,
            color: Cesium.Color.ORANGE,
            outlineColor: Cesium.Color.DARKORANGE,
            outlineWidth: 2,
            distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, 6000000),
          },
          label: {
            text: '预报位置',
            font: '12px sans-serif',
            fillColor: Cesium.Color.YELLOW,
            outlineColor: Cesium.Color.DARKORANGE,
            outlineWidth: 2,
            style: Cesium.LabelStyle.FILL_AND_OUTLINE,
            verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
            pixelOffset: new Cesium.Cartesian2(0, -14),
            distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, 3000000),
          },
        });
        window.viewer.flyTo(this.forecastEntity, { duration: 0.5, offset: new Cesium.HeadingPitchRange(0, Cesium.Math.toRadians(-60), 500000) });
      } catch (e) {
        console.warn('预报位置实体创建失败:', e);
      }
    },

    /** 移除预报位置实体 */
    removeForecastEntity() {
      if (window.viewer && this.forecastEntity) {
        window.viewer.entities.remove(this.forecastEntity);
      }
      this.forecastEntity = null;
    },

    /** 加载预报数据（真实API） */
    async loadForecastData() {
      try {
        const { lng, lat } = this.forecastPanel.position;
        const response = await axios.get(PRESSURE_API_BASE, {
          params: {
            appkey: PRESSURE_APPKEY,
            cmd: 'datapointday',
            longitude: lng,
            latitude: lat,
            day: 7,
          },
        });
        if (!response.data || !response.data.success || !response.data.data) {
          this.forecastPanel.data = [];
          return;
        }
        const rawData = response.data.data;
        // 只取每天 2:00, 8:00, 14:00, 20:00 四个时间点
        const targetHours = [2, 8, 14, 20];
        const valid = (v) => v != null && v !== 9999;
        const grouped = {};
        const weekDays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
        rawData.forEach(item => {
          const t = new Date(item.time);
          const h = t.getHours();
          if (!targetHours.includes(h)) return;
          const d = new Date(t.getFullYear(), t.getMonth(), t.getDate());
          const key = d.toISOString().slice(0, 10);
          if (!grouped[key]) {
            const month = t.getMonth() + 1;
            const dayNum = t.getDate();
            const week = weekDays[t.getDay()];
            grouped[key] = {
              dateKey: key,
              dateLabel: `${week} ${month}/${dayNum}`,
              weekDay: week,
              dateShort: `${month}-${dayNum}`,
              hours: [],
            };
          }
          grouped[key].hours.push({
            hour: h,
            temperature: valid(item.temp) ? item.temp : null,
            windSpeed: valid(item.wind_speed) ? item.wind_speed : null,
            gustSpeed: valid(item.gust) ? item.gust : null,
            windDirection: valid(item.wind_direction) ? item.wind_direction : null,
            waveHeight: valid(item.wave_height) ? item.wave_height : null,
          });
        });
        // 按日期排序并补全缺失的小时
        const result = Object.values(grouped).sort((a, b) => a.dateKey.localeCompare(b.dateKey));
        result.forEach(day => {
          const existingHours = day.hours.map(h => h.hour);
          targetHours.forEach(h => {
            if (!existingHours.includes(h)) {
              day.hours.push({ hour: h, temperature: null, windSpeed: null, gustSpeed: null, windDirection: null, waveHeight: null });
            }
          });
          day.hours.sort((a, b) => a.hour - b.hour);
        });
        this.forecastPanel.data = result.slice(0, 7);
      } catch (e) {
        console.error('天气预报数据加载失败:', e);
        this.forecastPanel.data = [];
      }
    },

    getTempClass(temp) {
      if (temp == null) return '';
      if (temp >= 35) return 'temp-severe';
      if (temp >= 30) return 'temp-hot';
      if (temp >= 25) return 'temp-warm';
      if (temp >= 18) return 'temp-mild';
      if (temp >= 10) return 'temp-cool';
      if (temp >= 0) return 'temp-cold';
      return 'temp-freeze';
    },

    getWindClass(speed) {
      if (speed == null) return '';
      if (speed >= 12) return 'wind-severe';
      if (speed >= 8) return 'wind-strong';
      if (speed >= 5) return 'wind-mid';
      if (speed >= 3) return 'wind-light';
      return 'wind-weak';
    },

    // ========== 清理 ==========

    weatherBeforeDestroy() {
      this.removeTimeline();
      this.clearIsolinePrimitives();
      this.unbindClick();
      this.removePressureLegend();
      this.removeTemperatureLayer();
      this.removeTemperatureLegend();
      this.removeWaveLayer();
      this.removeWaveLegend();
      this.removeTyphoonPanel();
      this.clearTyphoonTrack();
      this.removeTyphoonGifOverlay();
      this.removeTidePoints();
      this.removeWindLayer();
      this.removeWindLegend();
      this.closeForecastPanel();
      if (this._tideScript) { this._tideScript.remove(); this._tideScript = null; }
    },
  },
};
